/**
 * Comp `.rail-foot` — the bottom of the rail: the way to the detailed view and who is signed in.
 *
 * Help lives in each surface's header (`SimpleHelpButton`), and the simple view has no Settings (owner 2026-10-31).
 *
 * "Detailed view" is an operator affordance, not a trader one: it is shown only
 * to users who can actually see agents AND run one of the builder surfaces the
 * detailed shell exists for.
 */
import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Avatar } from '../../components/ui/Avatar.js';
import { Button } from '../../components/ui/Button.js';
import { usePolicy, useAnyPolicy } from '../../hooks/usePolicy.js';
import { useUiMode } from '../../hooks/useUiMode.js';
import { useAuthStore } from '../../store/index.js';
import { railRowClass } from './rail-row.js';

interface Props {
  /** The conversation the landing currently has open, if any. */
  selectedConversationId: string | null;
  /** The rail is expanded; collapsed, only the avatar shows. */
  expanded?: boolean;
}

export const SimpleRailFooter: React.FC<Props> = ({ selectedConversationId, expanded = true }) => {
  const navigate = useNavigate();
  const { setUiMode } = useUiMode();
  const user = useAuthStore((s) => s.user);

  const canViewAgents = usePolicy('CanViewAgents');
  const canBuild = useAnyPolicy('CanManageWorkflows', 'CanManageWorkforce', 'RequireSuperadmin');
  const showDetailedLink = canViewAgents && canBuild;

  const displayName = user?.fullName || user?.username || user?.email || 'You';
  const org = user?.organizationName ?? null;

  if (!expanded) {
    return (
      <div className="mt-2 flex flex-none justify-center border-t pt-3">
        <Avatar kind="human" label={displayName} size={28} />
      </div>
    );
  }

  return (
    <div className="mt-2 flex flex-none flex-col gap-0.5 border-t pt-3">
      {showDetailedLink && (
        <Button
          variant="ghost"
          className={railRowClass()}
          onClick={() => {
            setUiMode('detailed');
            // Carry the open conversation across the switch — the detailed shell shows it
            // at its own route. Landing on the dashboard would lose what the person was
            // reading (`?c=<id>` is the simplified landing's selection).
            navigate(selectedConversationId ? `/conversations/${selectedConversationId}` : '/');
          }}
        >
          <span>Detailed view</span>
        </Button>
      )}

      <div className="flex items-center gap-2.5 px-2.5 py-2">
        <Avatar kind="human" label={displayName} size={28} />
        <div className="min-w-0">
          <div className="truncate text-[13px] text-body">{displayName}</div>
          {org && <small className="block truncate text-[11px] text-faint">{org}</small>}
        </div>
      </div>
    </div>
  );
};
